import { useEffect, useState } from 'react'
import { getCachedText, preloadSiteContent } from '@/hooks/useSiteContent'

export default function Alignement() {
  const [, setTick] = useState(0)

  useEffect(() => {
    preloadSiteContent()
    const handler = () => setTick((n) => n + 1)
    window.addEventListener('site-content-updated', handler)
    return () => window.removeEventListener('site-content-updated', handler)
  }, [])

  const t = (key: string, fallback: string) => getCachedText('alignement', key, fallback)

  const axes = [
    {
      titre: t('axe_1_titre', 'Cohérence intérieure'),
      texte: t('axe_1_texte', 'On peut observer qu\'il existe en nous une forme d\'accord — ou de désaccord — entre ce que l\'on pense, ce que l\'on ressent, et ce que l\'on fait. Lorsque ces trois dimensions s\'accordent, quelque chose se détend. Quand elles divergent, le corps le sait souvent avant le mental.'),
      indice: t('axe_1_indice', 'La cohérence ne se force pas, elle s\'observe.')
    },
    {
      titre: t('axe_2_titre', 'Écoute du corps'),
      texte: t('axe_2_texte', 'Le corps parle par signaux subtils : tensions, relâchements, chaleurs, frissons, petites douleurs, soupirs. Dans l\'expérience, on peut remarquer que ces signes ne sont ni bons ni mauvais — ils sont des messages. Apprendre à les lire, sans chercher à les interpréter trop vite, peut favoriser une forme de dialogue plus fin.'),
      indice: t('axe_2_indice', 'Le corps n\'a pas besoin de mots pour se dire.')
    },
    {
      titre: t('axe_3_titre', 'Choix alignés'),
      texte: t('axe_3_texte', 'Certaines décisions laissent un sentiment de justesse, d\'autres un arrière-goût indéfinissable. On peut se demander : ce choix m\'ouvre-t-il ou me ferme-t-il ? Le corps le sait souvent. Se rendre disponible à cette écoute avant d\'agir peut favoriser une vie plus cohérente avec ce qu\'on est vraiment.'),
      indice: t('axe_3_indice', 'Un choix aligné n\'est pas facile — il est fidèle.')
    }
  ]

  return (
    <div className="fade-in">
      {/* Intro */}
      <section className="px-6 py-24 md:py-32">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs tracking-[0.4em] uppercase text-[#c9a96e] mb-8">
            {t('eyebrow', '— Espace intérieur —')}
          </p>
          <h1 className="font-serif text-5xl md:text-7xl font-light text-[#f5f1ea] mb-10">
            {t('page_title', 'Alignement')}
          </h1>
          <div className="w-16 h-px bg-[#c9a96e] mx-auto mb-10" />
          <p className="font-serif text-xl md:text-2xl text-[#f5f1ea]/80 leading-loose italic whitespace-pre-line">
            {t('page_intro', "Là où le pensé, le ressenti et l'agi\nse rencontrent sans forcer.")}
          </p>
        </div>
      </section>

      {/* Axes */}
      <section className="px-6 pb-32">
        <div className="max-w-4xl mx-auto space-y-24">
          {axes.map((axe, i) => (
            <article key={i} className="relative">
              {/* Decorative line */}
              <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-[#c9a96e]/40 via-[#c9a96e]/20 to-transparent" />

              <div className="pl-10 md:pl-16 space-y-6">
                <p className="text-xs tracking-[0.3em] uppercase text-[#c9a96e]/70">
                  {String(i + 1).padStart(2, '0')} — Axe
                </p>
                <h2 className="font-serif text-3xl md:text-5xl font-light text-[#f5f1ea]">
                  {axe.titre}
                </h2>
                <p className="text-base md:text-lg text-[#f5f1ea]/75 leading-loose font-light max-w-3xl">
                  {axe.texte}
                </p>
                <blockquote className="pt-4">
                  <p className="font-serif italic text-[#c9a96e]/90 text-xl leading-relaxed">
                    — {axe.indice}
                  </p>
                </blockquote>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Visual meditation */}
      <section className="px-6 py-32 border-t border-[#c9a96e]/10">
        <div className="max-w-3xl mx-auto text-center">
          <div className="relative w-48 h-48 mx-auto mb-16">
            <div className="absolute inset-0 rounded-full border border-[#c9a96e]/30 breathe" />
            <div className="absolute inset-4 rounded-full border border-[#c9a96e]/20 breathe" style={{ animationDelay: '2s' }} />
            <div className="absolute inset-8 rounded-full border border-[#c9a96e]/15 breathe" style={{ animationDelay: '4s' }} />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-3 h-3 rounded-full bg-[#c9a96e] shimmer" />
            </div>
          </div>

          <p className="font-serif italic text-xl md:text-2xl text-[#f5f1ea]/80 leading-loose whitespace-pre-line">
            {t('closing_text', 'Au centre, quelque chose reste immobile.\nAutour, tout peut bouger.')}
          </p>
        </div>
      </section>
    </div>
  )
}
